import React, { useEffect, useState } from 'react';
import axios from 'axios';
import SellerLayout from './SellerLayout';

const SellerNoticeEdit = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState(null);

  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [isActive, setIsActive] = useState(false);
  const [sortOrder, setSortOrder] = useState(0);

  const [coupons, setCoupons] = useState([]);
  const [couponId, setCouponId] = useState('');

  const params = new URLSearchParams(window.location.search);
  const noticeId = params.get('id') || '';

  const fetchData = async () => {
    try {
      setLoading(true);

      const [noticeRes, couponRes] = await Promise.all([
        axios.get(`/api/seller/notices/detail?id=${encodeURIComponent(noticeId)}`, {
          withCredentials: true,
        }),
        axios.get('/api/seller/coupon/list', {
          withCredentials: true,
        }),
      ]);

      if (noticeRes.data?.redirect) {
        window.location.href = noticeRes.data.redirect;
        return;
      }

      if (noticeRes.data?.ok) {
        const row = noticeRes.data.notice || null;

        setNotice(row);
        setError(noticeRes.data.error || '');

        if (row) {
          setTitle(row.title || '');
          setContent(row.content || '');
          setIsActive(parseInt(row.is_active, 10) === 1);
          setSortOrder(row.sort_order ?? 0);

          setCouponId(
            row.linked_coupon_id
              ? String(row.linked_coupon_id)
              : row.coupon_id
                ? String(row.coupon_id)
                : ''
          );
        }
      }

      if (couponRes.data?.ok) {
        setCoupons(couponRes.data.coupons || []);
      }

    } catch (err) {
      if (err.response?.status === 401) {
        window.location.href =
          err.response.data?.redirect || '/seller/manage';
        return;
      }

      if (err.response?.status === 404) {
        alert('공지사항을 찾을 수 없습니다.');
        window.location.href = '/seller/notices';
        return;
      }

      console.error('셀러 공지 상세 로드 실패:', err);
      setError('데이터를 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [window.location.search]);

  const postAction = async (url, payload) => {
    try {
      const res = await axios.post(url, payload, {
        withCredentials: true,
      });

      if (res.data?.ok) {
        window.location.href = '/seller/notices';
      } else if (res.data?.redirect) {
        window.location.href = res.data.redirect;
      }
    } catch (err) {
      if (err.response?.status === 401) {
        window.location.href =
          err.response.data?.redirect || '/seller/manage';
        return;
      }

      const msg =
        err.response?.data?.error ||
        err.response?.data?.msg ||
        '처리 중 오류가 발생했습니다.';

      alert(msg);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!title.trim()) {
      alert('제목을 입력하세요.');
      return;
    }

    await postAction('/api/seller/notices/update', {
      id: noticeId,
      title,
      content,
      is_active: isActive ? 1 : 0,
      sort_order: Number(sortOrder),

      // 쿠폰 해제 가능
      coupon_id: couponId ? Number(couponId) : null,
    });
  };

  return (
    <SellerLayout>
      <div className="container my-4" style={{ maxWidth: '900px' }}>

        <div className="d-flex justify-content-between align-items-center mb-3">
          <h4 className="mb-0">셀러 공지 수정</h4>
          <a href="/seller/notices" className="btn btn-outline-dark btn-sm">
            목록
          </a>
        </div>

        {error && (
          <div className="alert alert-danger py-2">{error}</div>
        )}

        <div className="card shadow-sm">
          <div className="card-body">

            {loading ? (
              <div className="text-center py-4 text-muted">
                불러오는 중...
              </div>
            ) : !notice ? (
              <div className="text-center py-4 text-muted">
                공지사항을 찾을 수 없습니다.
              </div>
            ) : (
              <form onSubmit={handleSubmit}>

                <div className="mb-3">
                  <label className="form-label fw-semibold">제목</label>
                  <input
                    type="text"
                    className="form-control"
                    maxLength={200}
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    required
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label fw-semibold">내용</label>
                  <textarea
                    className="form-control"
                    rows={10}
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label fw-semibold">연결 쿠폰</label>
                  <select
                    className="form-select"
                    value={couponId}
                    onChange={(e) => setCouponId(e.target.value)}
                  >
                    <option value="">쿠폰 연결 안 함</option>

                    {coupons.map((coupon) => (
                      <option key={coupon.id} value={coupon.id}>
                        {coupon.coupon_name}
                        {' / '}
                        {coupon.discount_type === 'amount'
                          ? `${Number(coupon.discount_value).toLocaleString()}원 할인`
                          : `${coupon.discount_value}% 할인`}
                      </option>
                    ))}
                  </select>

                  <div className="form-text">
                    쿠폰 연결 안 함을 선택하고 저장하면 공지 상세의 쿠폰 받기 버튼이 사라집니다.
                  </div>
                </div>

                <div className="d-flex gap-4 align-items-center mb-3 flex-wrap">

                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      checked={isActive}
                      onChange={(e) => setIsActive(e.target.checked)}
                    />
                    <label className="form-check-label">
                      노출
                    </label>
                  </div>

                  <div className="d-flex align-items-center gap-2">
                    <label className="form-label mb-0 fw-semibold">
                      정렬
                    </label>

                    <input
                      type="number"
                      className="form-control"
                      value={sortOrder}
                      onChange={(e) => setSortOrder(e.target.value)}
                      style={{ width: '120px' }}
                    />

                    <span className="text-muted small">
                      높을수록 상단
                    </span>
                  </div>

                </div>

                <button type="submit" className="btn btn-dark">
                  저장
                </button>

              </form>
            )}

          </div>
        </div>

      </div>
    </SellerLayout>
  );
};

export default SellerNoticeEdit;